// src/components/SupportRequests/SupportRequestDetails.tsx
import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Typography,
  Box,
  TextField,
  Paper,
  Divider,
  Avatar,
  IconButton,
  Grid,
  CircularProgress,
  Chip
} from '@mui/material';
import { 
  SendRounded, 
  AttachFileRounded, 
  PersonRounded,
  SupportAgentRounded,
  CloseRounded
} from '@mui/icons-material';
import { 
  updateSupportRequest, 
  markMessagesAsRead,
  closeSupportRequest
} from '../../services/SupportService.ts';
import { SupportRequest, SupportRequestConversation } from './types';

interface MessageBubbleProps {
  message: SupportRequestConversation;
  isAdmin: boolean;
}

const StatusChip: React.FC<{ status: string }> = ({ status }) => {
  const statusConfig: Record<string, { color: 'primary' | 'default' | 'warning' | 'info' | 'error'; label: string }> = {
    'open': { color: 'primary', label: 'Open' },
    'closed': { color: 'default', label: 'Closed' },
    'waiting_for_customer': { color: 'warning', label: 'Waiting for Customer' },
    'in_progress': { color: 'info', label: 'In Progress' }
  };
  
  const config = statusConfig[status] || { color: 'default', label: status };
  
  return <Chip label={config.label} color={config.color} size="small" />;
};

// Component for displaying individual messages
const MessageBubble: React.FC<MessageBubbleProps> = ({ message, isAdmin }) => {
  const isUser = message.senderType === 'user';
  
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: isUser ? 'flex-start' : 'flex-end',
        mb: 2,
        maxWidth: '100%'
      }}
    >
      {isUser && (
        <Avatar sx={{ bgcolor: 'primary.main', mr: 1 }}>
          <PersonRounded />
        </Avatar>
      )}
      
      <Box
        sx={{
          maxWidth: '70%',
          minWidth: '100px'
        }}
      >
        <Paper
          elevation={1}
          sx={{
            p: 2,
            backgroundColor: isUser ? 'grey.100' : 'primary.light',
            color: isUser ? 'text.primary' : 'common.white',
            borderRadius: 2,
            position: 'relative'
          }}
        >
          {/* Unread indicator */}
          {(isUser && !message.isReadByAdmin) && (
            <Box 
              sx={{ 
                position: 'absolute', 
                top: -8, 
                right: -8, 
                width: 16, 
                height: 16, 
                borderRadius: '50%', 
                bgcolor: 'error.main' 
              }} 
            />
          )}
          
          {(!isUser && !message.isReadByUser) && (
            <Typography 
              variant="caption" 
              sx={{ 
                position: 'absolute',
                top: -20,
                right: 10,
                color: 'info.main',
                fontStyle: 'italic'
              }}
            >
              Not read by user
            </Typography>
          )}
          
          <Typography variant="body1">{message.body}</Typography>
          
          {message.files && message.files.length > 0 && (
            <Box sx={{ mt: 1 }}>
              {message.files.map((file, index) => (
                <Button
                  key={index}
                  size="small"
                  startIcon={<AttachFileRounded />}
                  href={file.url}
                  target="_blank"
                  sx={{ mt: 0.5 }}
                >
                  {file.name}
                </Button>
              ))}
            </Box>
          )}
          
          <Typography 
            variant="caption" 
            sx={{ 
              display: 'block', 
              mt: 0.5,
              textAlign: 'right',
              color: isUser ? 'text.secondary' : 'rgba(255, 255, 255, 0.7)'
            }}
          >
            {new Date(message.created_at).toLocaleTimeString()} • {new Date(message.created_at).toLocaleDateString()}
          </Typography>
        </Paper>
      </Box>
      
      {!isUser && (
        <Avatar sx={{ bgcolor: 'secondary.main', ml: 1 }}>
          <SupportAgentRounded />
        </Avatar>
      )}
    </Box>
  );
};

interface SupportRequestModalProps {
  open: boolean;
  onClose: (refreshData?: boolean) => void;
  request: SupportRequest;
}

const SupportRequestModal: React.FC<SupportRequestModalProps> = ({ 
  open, 
  onClose, 
  request 
}) => {
  const [status, setStatus] = useState<string>(request?.status || 'open');
  const [newMessage, setNewMessage] = useState<string>('');
  const [files, setFiles] = useState<File[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [conversations, setConversations] = useState<SupportRequestConversation[]>(
    request?.conversations || []
  );
  
  const messageEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Scroll to bottom of messages
  useEffect(() => {
    if (messageEndRef.current) {
      messageEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [conversations]);
  
  // Mark messages as read when opened
  useEffect(() => {
    if (open && request) {
      const hasUnreadMessages = conversations.some(msg => !msg.isReadByAdmin);
      
      if (hasUnreadMessages) {
        markMessagesAsRead(request.id)
          .then(() => {
            // Update local state to mark messages as read
            setConversations(prev => 
              prev.map(msg => ({
                ...msg,
                isReadByAdmin: true
              }))
            );
          })
          .catch(err => {
            console.error('Failed to mark messages as read:', err);
          });
      }
    }
  }, [open, request]);

  const handleCloseTicket = async () => {
    try {
      setLoading(true);
      await closeSupportRequest(request.id);
      setStatus('closed');
      // Show success notification or feedback here
    } catch (error) {
      console.error('Failed to close ticket:', error);
      // Show error notification here
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      setFiles(Array.from(event.target.files));
    }
  };
  
  const handleSendMessage = async () => {
    if (!newMessage.trim() && files.length === 0) return;
    
    setLoading(true);
    
    try {
      const formData = new FormData();
      formData.append('message', newMessage);
      
      files.forEach(file => {
        formData.append('files', file);
      });
      
      const data = { body: newMessage, files: files };
      const response = await updateSupportRequest(request.id, data);
      
      if (response?.data) {
        // Add new message to conversation (assuming the response includes the new message)
        const newMessageObj: SupportRequestConversation = {
          id: Date.now(), // Temporary ID until we get the real one
          body: newMessage,
          senderType: 'admin',
          userId: 0, // Admin ID
          isReadByAdmin: true,
          isReadByUser: false,
          files: [],
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };
        
        setConversations(prev => [...prev, newMessageObj]);
        
        // Clear form
        setNewMessage('');
        setFiles([]);
      }
    } catch (error) {
      console.error('Failed to send message:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleClose = () => {
    onClose(true); // Pass true to refresh data
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Grid container justifyContent="space-between" alignItems="center">
          <Grid item>Support Request #{request?.id}</Grid>
          <Grid item>
            {status !== 'closed' && (
              <Button 
                variant="outlined" 
                color="error" 
                startIcon={<CloseRounded />}
                onClick={handleCloseTicket}
                disabled={loading}
              >
                Close Ticket
              </Button>
            )}
          </Grid>
        </Grid>
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6">{request?.subject}</Typography>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              From: {request?.userType === 'user' ? 'Customer' : 'Driver'} #{request?.userId || request?.driverId}
              {request?.userEmail && ` (${request.userEmail})`}
              {request?.driverEmail && ` (${request.driverEmail})`}
            </Typography>
            <StatusChip status={status} />
          </Box>
          {request?.rideId && (
            <Typography variant="body2" color="text.secondary">
              Related to Ride #{request.rideId}
            </Typography>
          )}
        </Box>
        
        <Divider sx={{ mb: 2 }} />
        
        {/* Messages area */}
        <Paper
          variant="outlined"
          sx={{
            height: '400px',
            p: 2,
            overflowY: 'auto',
            backgroundColor: 'background.default'
          }}
        >
          {conversations.length === 0 ? (
            <Typography variant="body2" color="text.secondary" align="center">
              No messages yet
            </Typography>
          ) : (
            conversations.map((message) => (
              <MessageBubble 
                key={message.id} 
                message={message}
                isAdmin={message.senderType === 'admin'}
              />
            ))
          )}
          <div ref={messageEndRef} />
        </Paper>
        
        {/* Message input area */}
        {status !== 'closed' && (
          <Box sx={{ mt: 2, display: 'flex', alignItems: 'flex-start' }}>
            <TextField
              fullWidth
              multiline
              rows={3}
              placeholder="Type your message here..."
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              variant="outlined"
              disabled={loading}
            />
            
            <Box sx={{ ml: 1, display: 'flex', flexDirection: 'column' }}>
              <input
                accept="image/*,.pdf,.doc,.docx,.txt"
                style={{ display: 'none' }}
                id="file-upload"
                type="file"
                multiple
                ref={fileInputRef}
                onChange={handleFileChange}
              />
              <label htmlFor="file-upload">
                <IconButton component="span" color="primary" disabled={loading}>
                  <AttachFileRounded />
                </IconButton>
              </label>
              
              <IconButton 
                color="primary" 
                onClick={handleSendMessage}
                disabled={(!newMessage.trim() && files.length === 0) || loading}
              >
                {loading ? <CircularProgress size={24} /> : <SendRounded />}
              </IconButton>
            </Box>
          </Box>
        )}
        
        {files.length > 0 && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="caption">Files to upload ({files.length}):</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {files.map((file, index) => (
                <Chip 
                  key={index}
                  label={file.name}
                  onDelete={() => setFiles(files.filter((_, i) => i !== index))}
                  size="small"
                />
              ))}
            </Box>
          </Box>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={handleClose} color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SupportRequestModal;